import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAdminBffClient } from './admin-bff-client';
import type {
  PackageRegistry,
  VulnerabilitySyncStatus,
  PackageVulnerability,
} from '@redbax/shared';

// Package Registries

export function useAdminPackageRegistries() {
  const client = useAdminBffClient();
  return useQuery({
    queryKey: ['admin', 'package-registries'],
    queryFn: () => client.get<PackageRegistry[]>('/bff/admin/package-registries'),
  });
}

export function useAdminPackageRegistry(id: string) {
  const client = useAdminBffClient();
  return useQuery({
    queryKey: ['admin', 'package-registries', id],
    queryFn: () => client.get<PackageRegistry>(`/bff/admin/package-registries/${id}`),
    enabled: !!id,
  });
}

export function useUpdateAdminPackageRegistry() {
  const client = useAdminBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; name: string; isEnabled: boolean }) =>
      client.put<PackageRegistry>(`/bff/admin/package-registries/${id}`, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'package-registries'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'package-registries', variables.id] });
    },
  });
}

export function useAddAdminPackageRegistryDomain() {
  const client = useAdminBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, domain }: { id: string; domain: string }) =>
      client.post(`/bff/admin/package-registries/${id}/domains`, { domain }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'package-registries', variables.id] });
    },
  });
}

export function useRemoveAdminPackageRegistryDomain() {
  const client = useAdminBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, domainId }: { id: string; domainId: string }) =>
      client.delete(`/bff/admin/package-registries/${id}/domains/${domainId}`),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'package-registries', variables.id] });
    },
  });
}

export function useToggleAdminPackageRegistry() {
  const client = useAdminBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      client.post(`/bff/admin/package-registries/${id}/toggle`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'package-registries'] });
    },
  });
}

// Vulnerability Sync

export function useVulnerabilitySyncStatus() {
  const client = useAdminBffClient();
  return useQuery({
    queryKey: ['admin', 'vulnerability-sync-status'],
    queryFn: () => client.get<VulnerabilitySyncStatus[]>('/bff/admin/vulnerability-sync/status'),
  });
}

export function useTriggerVulnerabilitySync() {
  const client = useAdminBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => client.postVoid('/bff/admin/vulnerability-sync/trigger'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'vulnerability-sync-status'] });
    },
  });
}

// Admin Vulnerability Search

export function useAdminVulnerabilities(params: {
  ecosystem?: string;
  packageName?: string;
  severity?: string;
}) {
  const client = useAdminBffClient();
  const searchParams = new URLSearchParams();
  if (params.ecosystem) searchParams.set('ecosystem', params.ecosystem);
  if (params.packageName) searchParams.set('packageName', params.packageName);
  if (params.severity) searchParams.set('severity', params.severity);
  const query = searchParams.toString();
  const path = query ? `/bff/admin/vulnerabilities?${query}` : '/bff/admin/vulnerabilities';

  return useQuery({
    queryKey: ['admin', 'vulnerabilities', params],
    queryFn: () => client.get<PackageVulnerability[]>(path),
    enabled: !!(params.ecosystem || params.packageName || params.severity),
  });
}
